import React, { useState, useRef } from "react";
import { Editor as SlateEditor, createEditor } from "slate";
import { withHistory } from "slate-history";
import { withReact } from "slate-react";

const get = (markdown) => {
    //  get function takes in a markdown string and returns nodes
    const lines = markdown.split("\n");
    const nodes = [];

    lines.forEach((line) => {
        let text = line;
        let bold = false;
        let italic = false;
        let underline = false;
        let code = false;

        while (text.includes("**")) {
            bold = !bold;
            text = text.replace("**", "");
        }
        while (text.includes("*")) {
            italic = !italic;
            text = text.replace("*", "");
        }
        while (text.includes("`")) {
            code = !code;
            text = text.replace("`", "");
        }
        if (text.includes("__")) {
            underline = true;
            text = text.replace("__", "");
        }

        const leaf = { text, bold, italic, underline, code };
        nodes.push({
            type: "paragraph",
            children: [{ text, bold, italic, underline, code }],
        });
    });

    return nodes;
};

const set = (nodes) => {
    let markdown = "";

    nodes.forEach((node) => {
        const children = node.children;

        children.forEach((child) => {
            let text = child.text;
            if (child.bold) {
                text = "**" + text + "**";
            }
            if (child.italic) {
                text = "*" + text + "*";
            }
            if (child.underline) {
                text = "__" + text + "__";
            }
            if (child.code) {
                text = "`" + text + "`";
            }
            markdown += text;
        });

        markdown += "\n";
    });

    return markdown;
};

const Toolbar = ({ editor, onMarkdownChange }) => {
    const handleBoldClick = () => {
        const markdown = set(editor.children);
        const newMarkdown = markdown.replace(/\*\*(.*?)\*\*/g, (match, p1) => {
            return "**" + p1 + "**";
        });
        onMarkdownChange(newMarkdown);
    };

    const handleItalicClick = () => {
        const markdown = set(editor.children);
        const newMarkdown = markdown.replace(/\*(.*?)\*/g, (match, p1) => {
            return "*" + p1 + "*";
        });
        onMarkdownChange(newMarkdown);
    };

    const handleUnderlineClick = () => {
        const markdown = set(editor.children);
        const newMarkdown = markdown.replace(/__(.*?)__/g, (match, p1) => {
            return "__" + p1 + "__";
        });
        onMarkdownChange(newMarkdown);
    };

    const handleCodeClick = () => {
        const markdown = set(editor.children);
        const newMarkdown = markdown.replace(/`(.*?)`/g, (match, p1) => {
            return "`" + p1 + "`";
        });
        onMarkdownChange(newMarkdown);
    };

    return (
        <div className="toolbar">
            <button onClick={handleBoldClick}>Bold</button>
            <button onClick={handleItalicClick}>Italic</button>
            <button onClick={handleUnderlineClick}>Underline</button>
            <button onClick={handleCodeClick}>Code</button>
        </div>
    );
};

const SlateEditorComponent = () => {
    const [value, setValue] = useState("");
    const editor = useRef(null);

    const handleContentChange = (value) => {
        setValue(value);
    };

    const handleInput = (e) => {
        const text = e.target.value;
        setValue(text);
    };

    React.useEffect(() => {
        editor.current = withReact(withHistory(createEditor()));
    }, []);

    const renderElement = (props) => {
        switch (props.element.type) {
            case "bold":
                return <strong {...props.attributes}>{props.children}</strong>;
            case "italic":
                return <em {...props.attributes}>{props.children}</em>;
            case "underline":
                return <u {...props.attributes}>{props.children}</u>;
            case "code":
                return <code {...props.attributes}>{props.children}</code>;
            default:
                return <p {...props.attributes}>{props.children}</p>;
        }
    };

    const renderLeaf = (props) => {
        let leaf = props.leaf;
        let text = props.children[0];

        if (leaf.bold) {
            text = <strong>{text}</strong>;
        }
        if (leaf.italic) {
            text = <em>{text}</em>;
        }
        if (leaf.underline) {
            text = <u>{text}</u>;
        }
        if (leaf.code) {
            text = <code>{text}</code>;
        }

        return <span {...props.attributes}>{text}</span>;
    };

    return (
        <div className="editor">
            <textarea value={value} onChange={handleInput} />
            <Toolbar
                editor={editor.current}
                value={value}
                onChange={handleContentChange}
            />
            <SlateEditor
                ref={editor}
                value={get(value)}
                onChange={(value) => setValue(set(value))}
                renderElement={renderElement}
                renderLeaf={renderLeaf}
            />
        </div>
    );
};

export default SlateEditorComponent;
